import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PropertyStructuredData } from '../components/seo/PropertyStructuredData';
import { Helmet } from 'react-helmet';
import axios from 'axios';

interface Property {
  mlsNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  squareFeet: number;
  description: string;
  photos: string[];
  listingDate: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

export const PropertyDetail: React.FC = () => {
  const { mlsNumber, shortLink } = useParams<{ mlsNumber?: string; shortLink?: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [actualMlsNumber, setActualMlsNumber] = useState<string | null>(null);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        let propertyId = mlsNumber;
        
        // If we have a short link, try to resolve it first
        if (shortLink && !mlsNumber) {
          // For now, we'll redirect to search with the short link
          // In production, you'd want to resolve the short link to the actual MLS number
          navigate(`/search?q=${shortLink}`);
          return;
        }
        
        if (propertyId) {
          const response = await axios.get(`/api/mls-portal/property/${propertyId}`);
          setProperty(response.data);
          setActualMlsNumber(propertyId);
        }
      } catch (error) {
        console.error('Error fetching property:', error);
        // If property not found, redirect to search
        navigate('/search');
      } finally {
        setLoading(false);
      }
    };

    if (mlsNumber || shortLink) {
      fetchProperty();
    }
  }, [mlsNumber, shortLink, navigate]);

  const handleChatClick = () => {
    // Store property context for AI chat
    sessionStorage.setItem('propertyContext', JSON.stringify({
      mlsNumber,
      address: property?.address,
      city: property?.city
    }));
    // Navigate to existing search with property context
    navigate(`/search?mlsNumber=${mlsNumber}&view=details`);
  };

  if (loading) return <div>Loading...</div>;
  if (!property) return <div>Property not found</div>;

  return (
    <>
      <Helmet>
        <title>{property.address}, {property.city}, {property.state} | {window.location.hostname}</title>
        <meta name="description" content={`${property.bedrooms} bed, ${property.bathrooms} bath home in ${property.city}, ${property.state}. ${property.description}`} />
        <link rel="canonical" href={`${window.location.origin}/property/${actualMlsNumber || mlsNumber}`} />
      </Helmet>

      <PropertyStructuredData
        mlsNumber={property.mlsNumber}
        address={property.address}
        city={property.city}
        state={property.state}
        zipCode={property.zipCode}
        price={property.price}
        bedrooms={property.bedrooms}
        bathrooms={property.bathrooms}
        squareFeet={property.squareFeet}
        description={property.description}
        images={property.photos}
        listingDate={property.listingDate}
      />

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Property Images Gallery */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {property.photos.slice(0, 4).map((photo, index) => (
              <img 
                key={index}
                src={photo} 
                alt={`${property.address} - Photo ${index + 1}`}
                className="w-full h-64 object-cover rounded-lg"
              />
            ))}
          </div>

          {/* Property Details */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h1 className="text-3xl font-bold mb-2">{property.address}</h1>
            <p className="text-xl text-gray-600 mb-4">{property.city}, {property.state} {property.zipCode}</p>
            
            <div className="flex items-center justify-between mb-6">
              <div className="text-3xl font-bold text-green-600">
                ${property.price.toLocaleString()}
              </div>
              <div className="flex space-x-4 text-gray-600">
                <span>{property.bedrooms} beds</span>
                <span>{property.bathrooms} baths</span>
                <span>{property.squareFeet.toLocaleString()} sqft</span>
              </div>
            </div>

            <p className="text-gray-700 mb-6">{property.description}</p>

            {/* CTA to AI Chat */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <h3 className="text-xl font-semibold mb-2">Want to know more?</h3>
              <p className="text-gray-600 mb-4">
                Chat with our AI assistant to get detailed insights about this property, 
                neighborhood information, and schedule a viewing.
              </p>
              <button
                onClick={handleChatClick}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                View in AI-Powered Search
              </button>
            </div>
          </div>

          {/* MLS Compliance Footer */}
          <div className="text-sm text-gray-500 text-center">
            <p>© 2024 Arizona Regional Multiple Listing Service, Inc. All rights reserved.</p>
            <p>All information should be verified by the recipient and none is guaranteed as accurate by ARMLS.</p>
          </div>
        </div>
      </div>
    </>
  );
};